/**
 * A product's SKU with its "SKU" title in front of it ("SKU: 9115").
 *
 * Presentational only: whether a SKU may be shown at all (B2B members,
 * single-version products) is decided by `useProductSku`, so the product card
 * and the quick order apply the same rule. This component only prints what it
 * is given.
 */

import React from "react";
import { useLocalizedTexts, t } from "@/utils/translations";
import styles from "./ProductSku.module.css";

/** English title, also used when an editor has blanked the translation. */
const SKU_TITLE_FALLBACK = "SKU";

interface ProductSkuProps {
  /** The SKU to print, or null/undefined to print nothing. */
  sku: string | null | undefined;
  /** Placement only; colour and size come from the custom properties in ProductSku.module.css. */
  className?: string;
}

export const ProductSku: React.FC<ProductSkuProps> = ({ sku, className }) => {
  const localizedTexts = useLocalizedTexts();

  if (!sku) return null;

  // `t()` returns an empty translation as-is, which would leave a bare ": 9115".
  const title =
    t(localizedTexts, "Tracker.Products.Sku", SKU_TITLE_FALLBACK) ||
    SKU_TITLE_FALLBACK;

  return (
    <p
      className={[styles.sku, className].filter(Boolean).join(" ")}
      data-component="product-sku"
    >
      {title}:{" "}
      {/* A product code must survive the browser's page translation untouched. */}
      <span className={styles.value} translate="no">
        {sku}
      </span>
    </p>
  );
};

export default ProductSku;
